/**
 * Per-card weight problems for the Rules tab: a BLOCKING error on any weight
 * group with a broken payload, and an ADVISORY warning on every static group
 * of a layer whose pins could together exceed 100% (the engine blocks that
 * per pool only when the rules actually co-match; see shared weight helpers).
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  WEIGHT_RULE_PERCENT_MAX,
  WEIGHT_RULE_PERCENT_MIN,
  parseRuleWeightConfig,
} from '@trafficpulse/shared';
import type { FlowLayer } from '@trafficpulse/shared';

import {
  ALL_LAYERS,
  type ExcludeView,
  type ExcludeViewGroup,
} from '../../../../utils/exclude-view-model';

export interface WeightIssue {
  level: 'error' | 'warning';
  text: string;
}

const hasWeightAction = (g: ExcludeViewGroup): boolean =>
  g.actions.includes('weight');

function markLayerWarning(
  out: Map<string, WeightIssue>,
  layer: FlowLayer,
  statics: ExcludeViewGroup[],
): void {
  const total = statics.reduce((sum, g) => sum + (g.weight?.percent ?? 0), 0);
  if (statics.length < 2 || total <= 100) return;
  for (const g of statics) {
    if (out.has(g.id)) continue;
    out.set(g.id, {
      level: 'warning',
      text:
        `Static pins on ${layer} total ${total}% — if these rules match in ` +
        `the same pool, preview and apply will be blocked until they fit 100%.`,
    });
  }
}

/** Map of group id → the problem to show on that rule card. */
export function weightIssueByGroupId(view: ExcludeView): Map<string, WeightIssue> {
  const out = new Map<string, WeightIssue>();
  const weighted = view.filter(hasWeightAction);
  for (const g of weighted) {
    if (parseRuleWeightConfig(g.weight)) continue;
    out.set(g.id, {
      level: 'error',
      text:
        `Pick a mode and a whole-number percent between ` +
        `${WEIGHT_RULE_PERCENT_MIN} and ${WEIGHT_RULE_PERCENT_MAX} — the rule ` +
        `cannot be saved until then.`,
    });
  }
  for (const layer of ALL_LAYERS) {
    const statics = weighted.filter(
      (g) =>
        g.appliesTo.includes(layer) &&
        parseRuleWeightConfig(g.weight)?.mode === 'static',
    );
    markLayerWarning(out, layer, statics);
  }
  return out;
}
